define((require) => {
    "use strict";

    const ChunkingCollectionQuery         = require("data/ChunkingCollectionQuery");
    const Class                           = require("shared/util/Class");
    const FilterQuery                     = require("data/FilterQuery");
    const IdValidator                     = require("util/IdValidator");
    const _                               = require("underscore");

    const FilterExpression = Class.extend("FilterExpression", {

        name: null,

        constructor() {
            this._expression = {};
        },

        field(field) {
            this._expression.field = field;

            return this;
        },

        value(value) {
            this._expression.value = value;

            return this;
        },

        toJSON() {
            const json = {};

            json[this.name] = this._expression;

            return json;
        }

    });

    const OperationExpression = FilterExpression.extend("OperationExpression", {

        name: "Operation",

        op(op) {
            this._expression.op = op;

            return this;
        }

    });

    const SortQuery = ChunkingCollectionQuery.extend("SortQuery", {

        constructor() {
            this.__super();

            this._query = [];
        },

        field(value, direction) {
            this._query.push({
                field: value,
                direction: direction === undefined ? "Descending" : direction
            });

            return this;
        },

        querify() {
            return {
                sort: JSON.stringify(this._query)
            };
        }

    });


    /**
     * The "root" query returned by <tt>ContentQuery.query</tt> - combines a filter and sort query.
     *
     * @class RootQuery
     */
    const RootQuery = ChunkingCollectionQuery.extend("RootQuery", {

        /**
         * Constructor.
         *
         * @param {FilterQuery} filter The required <tt>FilterQuery</tt>.
         * @param {SortQuery} sort Optional <tt>SortQuery</tt>.
         */
        constructor(filter, sort) {
            this.__super();

            this._filter = filter;
            this._sort = sort;
        },

        querify() {
            const json = this._filter.querify();

            if (this._sort) {
                _.extend(json, this._sort.querify());
            }

            return json;
        }

    });

    const tagNameExpression = (name) => new OperationExpression()
        .value(name)
        .field("tags.name")
        .op("~=");

    const nameExpression = (name) => new OperationExpression()
        .value(name)
        .field("name")
        .op("~=");

    const ContentQuery = {

        filter() {
            return new FilterQuery();
        },

        sort() {
            return new SortQuery();
        },

        /**
         * Constructs a <tt>RootQuery</tt> from the specified filter and optional sorting query.
         *
         * @param {FilterQuery} filter The required <tt>FilterQuery</tt>.
         * @param {SortQuery} sort Optional <tt>SortQuery</tt>.
         *
         * @returns A <tt>RootQuery</tt> instance.
         */
        query(filter, sort) {
            return new RootQuery(filter, sort);
        },

        /**
         * Convenience for constructing a search query. By default, the passed value will be matched
         * against the <tt>name</tt> field.<p>
         *
         * If a valid MongoDB id is passed, we explicitly search for <tt>id</tt>.
         *
         * @param  {String|String[]} value The search phrase, array of phrases or content id (as a valid mongoDB id).
         * @param  {SortQuery} sortQuery
         * @param  {includeTagName} Boolean If true include tag name in the query
         *
         * @return {ChunkingCollectionQuery} The search query.
         */
        search(value, sortQuery, includeTagName = true) {
            const sorting = sortQuery || ContentQuery.sort().field("editedAt");

            if (Array.isArray(value)) {
                const query = new FilterQuery(FilterQuery.TYPE_AND);

                value.forEach((phrase) => {
                    const name = nameExpression(phrase);
                    const expressions = [name];

                    if (includeTagName) {
                        const tag = tagNameExpression(phrase);
                        expressions.push(tag);

                        if (value.length > 1) {
                            const phrase = value.join(" ");
                            expressions.push(tagNameExpression(phrase));
                        }

                        query.or(...expressions);
                    } else {
                        // If search tags aren't enabled there is no use for nested operators since we only search for one attribute (name).
                        // Also, since some sources (e.g. Adobe Scene7) don't have tag names nor allow querying with nested operators
                        // the searchTags boolean is used to determine whether to only use the root query type as well.
                        query.useRoot(...expressions);
                    }
                });

                return ContentQuery.query(query, sorting);
            }

            const expression = new OperationExpression().value(value);

            // Search specifically for id if one is specified.
            if (IdValidator.isValid(value)) {
                expression.field("id").op("=");
            } else {
                expression.field("name").op("~=");
            }

            return ContentQuery.query(ContentQuery.filter().and(expression), sorting);
        }
    };

    ContentQuery.ALL = ContentQuery.filter();

    return ContentQuery;
});



// WEBPACK FOOTER //
// ./src/editor/data/ContentQuery.js